/**
 * Dispatch API Client
 *
 * Handles communication with the dispatcher dashboard backend.
 * All endpoints use the /api3.2/dispatch prefix.
 *
 * Backend: CANapps-unix/src/routes/dispatchGuardLocations.ts
 */

import axiosInstance from '../axiosInstance';
import {
  DashboardStats,
  ActivityFeedResponse,
  ActivityFeedParams,
  AlertsResponse,
  AlertsParams,
  DismissAlertResponse,
  CompletePatrolRequest,
  CompletePatrolResponse,
  StartPatrolRequest,
  StartPatrolResponse,
  IncompletePatrolsResponse,
  GuardLocationsResponse,
  GuardWithLocationResponse,
  GuardContactResponse,
  NotificationSettings,
  UpdateNotificationSettingsRequest,
  TestNotificationRequest,
  TestNotificationResponse,
  VapidPublicKeyResponse,
  PushSubscriptionData,
  SavePushSubscriptionResponse,
  RemovePushSubscriptionResponse,
  PushSubscriptionStatus,
  GuardCurrentShiftResponse,
  PatrolLocationsApiResponse,
  PatrolLocationsParams,
  SitePatrolsResponse,
  SitePatrolsParams,
} from './types';
import {
  GuardLocationRaw,
  GuardLocationsResponse as FrontendGuardLocationsResponse,
} from '../../containers/pages/CANguard/components/guardLocation/types';

// ============================================
// Configuration
// ============================================

/**
 * Helper function to determine API URL based on environment
 */
const getApiUrl = (endpoint: string): string => {
  if (process.env.NODE_ENV === 'production') {
    return `/api3.2/dispatch${endpoint}`;
  }

  // In development, check if we're running through IIS (port 8000) or direct (port 3000)
  if (window.location.port === '8000') {
    return `/api3.2/dispatch${endpoint}`;
  }

  return `http://localhost:10000/api3.2/dispatch${endpoint}`;
};

/**
 * Helper function to get X-Forwarded-Host header for multitenancy
 */
const getForwardedHost = (apiUrl: string): string => {
  try {
    const parsedUrl = apiUrl.startsWith('http')
      ? new URL(apiUrl)
      : new URL(apiUrl, window.location.origin);
    return parsedUrl.host;
  } catch {
    return window.location.host;
  }
};

/**
 * Unwrap backend response format: { success: true, data: <actual data> }
 */
function unwrapResponse<T>(response: { data: { success: boolean; data: T } | T }): T {
  const data = response.data;
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
    return data.data;
  }
  return data as T;
}

// ============================================
// Dashboard Stats (Issue 2.2)
// ============================================

/**
 * Fetch dashboard statistics
 *
 * Route: GET /api3.2/dispatch/dashboard-stats
 *
 * @param recentMinutes - Define "on duty" threshold (default: 30)
 */
export async function fetchDashboardStats(recentMinutes?: number): Promise<DashboardStats> {
  const API_URL = getApiUrl('/dashboard-stats');

  try {
    const response = await axiosInstance.get(API_URL, {
      params: { recentMinutes },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<DashboardStats>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to fetch dashboard stats:', error);
    throw error;
  }
}

// ============================================
// Activity Feed (Issue 2.4)
// ============================================

/**
 * Fetch activity feed events
 *
 * Route: GET /api3.2/dispatch/activity-feed
 *
 * @param params - Query parameters for filtering
 */
export async function fetchActivityFeed(
  params: ActivityFeedParams = {}
): Promise<ActivityFeedResponse> {
  const API_URL = getApiUrl('/activity-feed');

  try {
    const response = await axiosInstance.get(API_URL, {
      params: {
        limit: params.limit ?? 50,
        types: params.types,
        since: params.since,
      },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<ActivityFeedResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to fetch activity feed:', error);
    throw error;
  }
}

// ============================================
// Alerts (Issue 2.3)
// ============================================

/**
 * Fetch dispatcher alerts
 *
 * Route: GET /api3.2/dispatch/alerts
 *
 * @param params - Query parameters for filtering
 */
export async function fetchAlerts(params: AlertsParams = {}): Promise<AlertsResponse> {
  const API_URL = getApiUrl('/alerts');

  try {
    const response = await axiosInstance.get(API_URL, {
      params: {
        types: params.types,
        dismissed: params.dismissed ?? false,
        limit: params.limit ?? 100,
      },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<AlertsResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to fetch alerts:', error);
    throw error;
  }
}

/**
 * Dismiss an alert
 *
 * Route: POST /api3.2/dispatch/alerts/:alertId/dismiss
 *
 * @param alertId - The alert ID to dismiss
 */
export async function dismissAlert(alertId: number): Promise<DismissAlertResponse> {
  const API_URL = getApiUrl(`/alerts/${alertId}/dismiss`);

  try {
    const response = await axiosInstance.post(
      API_URL,
      {},
      {
        headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
      }
    );

    return unwrapResponse<DismissAlertResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to dismiss alert:', error);
    throw error;
  }
}

/**
 * Dismiss a VMS incident alert
 *
 * Route: POST /api3.2/dispatch/vms-alerts/:incidentId/dismiss
 *
 * @param incidentId - The VMS incident ID to dismiss
 */
export async function dismissVmsAlert(incidentId: number): Promise<DismissAlertResponse> {
  const API_URL = getApiUrl(`/vms-alerts/${incidentId}/dismiss`);

  try {
    const response = await axiosInstance.post(
      API_URL,
      {},
      {
        headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
      }
    );

    return unwrapResponse<DismissAlertResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to dismiss VMS alert:', error);
    throw error;
  }
}

// ============================================
// Patrol Completion (Issue 2.5)
// ============================================

/**
 * Complete a patrol via the C# ASMX backend.
 *
 * The GetScheduledItems SP marks a patrol complete when:
 *   comment_list_id IS NOT NULL AND end_date > start_date
 *
 * So we must ensure start_date is set before setting end_date.
 * 1. If start_date is missing, set it via UpdateDateDuration
 * 2. Call SubmitPatrol to set end_date and update task status
 *
 * @param commentListId - The comment_list_id of the patrol
 * @param hasStartDate - Whether the patrol already has a start_date
 * @param activityStatus - Current activity status (defaults to "Clear")
 */
export async function completePatrol(
  commentListId: number,
  hasStartDate: boolean = false,
  activityStatus: string = 'Clear',
): Promise<void> {
  const isProd = process.env.NODE_ENV === 'production';
  const prefix = isProd ? '/Portal' : '';

  try {
    // If patrol was never started, set start_date first
    // (complete requires end_date > start_date, and NULL > anything = false)
    if (!hasStartDate) {
      const now = new Date();
      // Set start_date to 1 minute before now so end_date > start_date
      const startDate = new Date(now.getTime() - 60000);
      const pad = (n: number) => String(n).padStart(2, '0');
      const startTime = `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())} ${pad(startDate.getHours())}:${pad(startDate.getMinutes())}`;

      let startUrl = `${prefix}/Project/WebService/CANDispatch.asmx/UpdateDateDuration`;
      startUrl += `?comment_list_id=${commentListId}&startOrEnd=start&time=${startTime}`;
      await axiosInstance.get(startUrl);
    }

    // Submit patrol to set end_date and update task.activity_id
    let url = `${prefix}/Project/WebService/CANDispatch.asmx/SubmitPatrol`;
    const response = await axiosInstance.post(url, {
      payload: {
        CommentListID: commentListId,
        UpdatedCheckpoints: [],
        UpdatedComment: null,
        UpdatedStatus: activityStatus,
        updateEndTime: true,
      },
    });
    if (response.status !== 200) {
      throw new Error('Failed to complete patrol');
    }
  } catch (error) {
    console.error('[Dispatch] Failed to complete patrol:', error);
    throw error;
  }
}

/**
 * Start a patrol on behalf of a guard
 *
 * Route: POST /api3.2/dispatch/patrols/:patrolId/start
 *
 * @param patrolId - The schedule_assignment_id of the patrol
 * @param request - Optional note explaining why dispatcher started the patrol
 */
export async function startPatrol(
  patrolId: number,
  request: StartPatrolRequest = {}
): Promise<StartPatrolResponse> {
  const API_URL = getApiUrl(`/patrols/${patrolId}/start`);

  try {
    const response = await axiosInstance.post(API_URL, request, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<StartPatrolResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to start patrol:', error);
    throw error;
  }
}

// ============================================
// Incomplete Patrols (Issue 2.6)
// ============================================

/**
 * Fetch incomplete patrols needing attention
 *
 * Route: GET /api3.2/dispatch/incomplete-patrols
 *
 * @param longRunningMinutes - Threshold for long-running detection (default: 240)
 * @param noPingMinutes - Threshold for no-ping detection (default: 120)
 */
export async function fetchIncompletePatrols(
  longRunningMinutes?: number,
  noPingMinutes?: number
): Promise<IncompletePatrolsResponse> {
  const API_URL = getApiUrl('/incomplete-patrols');

  try {
    const response = await axiosInstance.get(API_URL, {
      params: { longRunningMinutes, noPingMinutes },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<IncompletePatrolsResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to fetch incomplete patrols:', error);
    throw error;
  }
}

// ============================================
// Enhanced Guard Locations (Issue 2.1)
// ============================================

/**
 * Transform backend guard response to frontend format
 * Includes enhanced patrol progress data
 */
function transformGuardLocation(guard: GuardWithLocationResponse): GuardLocationRaw {
  return {
    guardId: guard.contactId,
    name: guard.name,
    latitude: guard.latitude,
    longitude: guard.longitude,
    timestamp: guard.lastSeenAt,
    minutesAgo: guard.minutesAgo,
    accuracy: null,
    batteryPercent: guard.batteryPercent,
    deviceInfo: guard.deviceInfo,
    currentPatrol: guard.currentAssignment
      ? {
          patrolId: guard.currentAssignment.scheduleAssignmentId,
          customerId: 0, // Not returned by backend
          customerName: guard.currentAssignment.customerName,
          siteId: 0, // Not returned by backend
          siteName: guard.currentAssignment.customerName,
          startTime: guard.currentAssignment.workDate,
          // Enhanced fields from Issue 2.1
          patrolStatus: guard.currentAssignment.patrolStatus,
          startedAt: guard.currentAssignment.startedAt,
          checkpointsCompleted: guard.currentAssignment.checkpointsCompleted,
          checkpointsTotal: guard.currentAssignment.checkpointsTotal,
        }
      : null,
  };
}

/**
 * Fetch enhanced guard locations with patrol progress
 *
 * Route: GET /api3.2/dispatch/guard-locations
 *
 * @param params - Query parameters for filtering
 */
export async function fetchGuardLocations(params: {
  customerId?: number;
  activeOnly?: boolean;
  staleMinutes?: number;
} = {}): Promise<FrontendGuardLocationsResponse> {
  const API_URL = getApiUrl('/guard-locations');

  try {
    const response = await axiosInstance.get<GuardLocationsResponse>(API_URL, {
      params: {
        customerId: params.customerId,
        activeOnly: params.activeOnly ?? true,
        staleMinutes: params.staleMinutes,
      },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    const data = unwrapResponse<GuardLocationsResponse>(response);

    return {
      guards: data.guards.map(transformGuardLocation),
      timestamp: data.timestamp,
      count: data.count,
    };
  } catch (error) {
    console.error('[Dispatch] Failed to fetch guard locations:', error);
    throw error;
  }
}

// ============================================
// Notification Settings (Issue 6.2)
// ============================================

/**
 * Fetch notification settings for the current dispatcher
 *
 * Route: GET /api3.2/dispatch/notification-settings
 *
 * Returns defaults if no settings exist yet
 */
export async function fetchNotificationSettings(): Promise<NotificationSettings> {
  const API_URL = getApiUrl('/notification-settings');

  try {
    const response = await axiosInstance.get(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<NotificationSettings>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to fetch notification settings:', error);
    throw error;
  }
}

/**
 * Update notification settings for the current dispatcher
 *
 * Route: PUT /api3.2/dispatch/notification-settings
 *
 * @param settings - Partial settings to update (only provided fields are updated)
 */
export async function updateNotificationSettings(
  settings: UpdateNotificationSettingsRequest
): Promise<NotificationSettings> {
  const API_URL = getApiUrl('/notification-settings');

  try {
    const response = await axiosInstance.put(API_URL, settings, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<NotificationSettings>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to update notification settings:', error);
    throw error;
  }
}

/**
 * Send a test notification to verify configuration
 *
 * Route: POST /api3.2/dispatch/notification-settings/test
 *
 * @param channel - The notification channel to test
 */
export async function sendTestNotification(
  channel: TestNotificationRequest['channel']
): Promise<TestNotificationResponse> {
  const API_URL = getApiUrl('/notification-settings/test');

  try {
    const response = await axiosInstance.post<TestNotificationResponse>(
      API_URL,
      { channel },
      {
        headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
      }
    );

    return unwrapResponse<TestNotificationResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to send test notification:', error);
    throw error;
  }
}

// ============================================
// Browser Push Subscriptions (Issue 6.4)
// ============================================

/**
 * Get VAPID public key for push subscription
 *
 * Route: GET /api3.2/dispatch/push/vapid-public-key
 *
 * Returns the VAPID public key needed by the browser to create a push subscription.
 * This is safe to call without authentication.
 */
export async function fetchVapidPublicKey(): Promise<VapidPublicKeyResponse> {
  const API_URL = getApiUrl('/push/vapid-public-key');

  try {
    const response = await axiosInstance.get(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<VapidPublicKeyResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to fetch VAPID public key:', error);
    throw error;
  }
}

/**
 * Save a push subscription to the server
 *
 * Route: POST /api3.2/dispatch/push-subscription
 *
 * @param subscription - The push subscription data from the browser
 */
export async function savePushSubscription(
  subscription: PushSubscriptionData
): Promise<SavePushSubscriptionResponse> {
  const API_URL = getApiUrl('/push-subscription');

  try {
    const response = await axiosInstance.post(API_URL, subscription, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<SavePushSubscriptionResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to save push subscription:', error);
    throw error;
  }
}

/**
 * Remove a push subscription from the server
 *
 * Route: DELETE /api3.2/dispatch/push-subscription
 *
 * @param endpoint - The push subscription endpoint URL to remove
 */
export async function removePushSubscription(
  endpoint: string
): Promise<RemovePushSubscriptionResponse> {
  const API_URL = getApiUrl('/push-subscription');

  try {
    const response = await axiosInstance.delete(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
      data: { endpoint },
    });

    return unwrapResponse<RemovePushSubscriptionResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to remove push subscription:', error);
    throw error;
  }
}

/**
 * Get push subscription status for the current user
 *
 * Route: GET /api3.2/dispatch/push-subscription/status
 *
 * Returns information about the user's push subscriptions.
 */
export async function fetchPushSubscriptionStatus(): Promise<PushSubscriptionStatus> {
  const API_URL = getApiUrl('/push-subscription/status');

  try {
    const response = await axiosInstance.get(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<PushSubscriptionStatus>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to fetch push subscription status:', error);
    throw error;
  }
}

// ============================================
// Guard Current Shift (Guard Summary Modal)
// ============================================

/**
 * Fetch guard's current shift patrols
 *
 * Route: GET /api3.2/dispatch/guards/:guardId/current-shift
 *
 * Returns the guard's current shift - patrols grouped by 1-hour gaps.
 * A shift is a group of patrols where each patrol starts within 1 hour
 * of the previous patrol's end time.
 *
 * @param guardId - The guard's contact_id
 */
export async function fetchGuardCurrentShift(
  guardId: number
): Promise<GuardCurrentShiftResponse> {
  const API_URL = getApiUrl(`/guards/${guardId}/current-shift`);

  try {
    const response = await axiosInstance.get(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<GuardCurrentShiftResponse>(response);
  } catch (error) {
    console.error(`[Dispatch] Failed to fetch guard ${guardId} current shift:`, error);
    throw error;
  }
}

// ============================================
// Guard Contact (Contact Guard Dialog)
// ============================================

/**
 * Fetch guard's contact information (phone numbers)
 *
 * Route: GET /api3.2/dispatch/guards/:guardId/contact
 *
 * Returns the guard's contact details including phone numbers
 * for the Contact Guard dialog in the dispatcher dashboard.
 *
 * @param guardId - The guard's contact_id
 */
export async function fetchGuardContact(
  guardId: number
): Promise<GuardContactResponse> {
  const API_URL = getApiUrl(`/guards/${guardId}/contact`);

  try {
    const response = await axiosInstance.get(API_URL, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<GuardContactResponse>(response);
  } catch (error) {
    console.error(`[Dispatch] Failed to fetch guard ${guardId} contact:`, error);
    throw error;
  }
}

// ============================================
// Patrol Locations Phase 2 - Filters & Site Info Modal
// ============================================

/**
 * Fetch patrol locations for map display
 *
 * Route: GET /api3.2/dispatch/patrol-locations
 *
 * Returns patrol locations with filtering by scope:
 * - 'active': Currently in-progress patrols (today only)
 * - 'today': All patrols for selected date
 * - 'all_sites': Sites with patrols in last 30 days
 *
 * @param params - Query parameters for filtering
 */
export async function fetchPatrolLocations(
  params: PatrolLocationsParams = {}
): Promise<PatrolLocationsApiResponse> {
  const API_URL = getApiUrl('/patrol-locations');

  try {
    const response = await axiosInstance.get(API_URL, {
      params: {
        date: params.date,
        scope: params.scope,
      },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<PatrolLocationsApiResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to fetch patrol locations:', error);
    throw error;
  }
}

/**
 * Fetch patrol history for a specific site
 *
 * Route: GET /api3.2/dispatch/locations/:addressId/patrols
 *
 * Returns site info, stats, today's patrols, and paginated history.
 *
 * @param addressId - The address/site ID
 * @param params - Query parameters for pagination and filtering
 */
export async function fetchSitePatrols(
  addressId: number,
  params: SitePatrolsParams = {}
): Promise<SitePatrolsResponse> {
  const API_URL = getApiUrl(`/locations/${addressId}/patrols`);

  try {
    const response = await axiosInstance.get(API_URL, {
      params: {
        limit: params.limit,
        offset: params.offset,
        fromDate: params.fromDate,
        toDate: params.toDate,
      },
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });

    return unwrapResponse<SitePatrolsResponse>(response);
  } catch (error) {
    console.error(`[Dispatch] Failed to fetch site ${addressId} patrols:`, error);
    throw error;
  }
}

// ============================================================================
// Dispatcher Messages to Guards
// ============================================================================

export interface SendMessageRequest {
  toContactId: number;
  message: string;
  scheduleAssignmentId?: number;
}

export interface SendMessageResponse {
  success: boolean;
  messageId: number;
  createdAt: string;
}

/**
 * Send a direct message from dispatcher to a guard.
 * Triggers push notification to guard's mobile device.
 */
export async function sendMessageToGuard(
  request: SendMessageRequest
): Promise<SendMessageResponse> {
  const API_URL = getApiUrl('/messages');

  try {
    const response = await axiosInstance.post(API_URL, request, {
      headers: { 'X-Forwarded-Host': getForwardedHost(API_URL) },
    });
    return unwrapResponse<SendMessageResponse>(response);
  } catch (error) {
    console.error('[Dispatch] Failed to send message to guard:', error);
    throw error;
  }
}
